//src/pages/inventory/Pharmacy/IpdIssueMedicineModal.jsx
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { getMedicineInventoryList } from "../../../services/medicineInventoryService";
import { createInvoice } from "../../../services/invoiceService";
import { getCostPerUnitById } from "../../../services/medicineNewService";
import { deductMedicineInventoryWithHistory } from "../../../services/medicineTransactionService";
import { resolveInvoiceDepartmentId, resolveInvoicePrescriptionId } from "../../../utils/invoiceRelationUtils";
import { useAuth } from "../../../context/AuthContext";
import { Alert, AlertTitle } from '@mui/material';
import { usePharmacyExpiryThreshold } from "../../../hooks/usePharmacyExpiryThreshold";
import { getOrganizationName, getOrganizationId } from "../../../utils/organizationUtils";
import { triggerNearExpiryLowStockAlert } from "../../../services/pharmacySettingsService";
import { formatExpiryThresholdLabel } from "../../../utils/medicineExpiryThreshold";
import {
  evaluateMedicineIssueStock,
  isUsableInventoryBatch,
  resolveMedicineInventoryMatch,
  stockStatusLabel,
} from "../../../utils/medicineIssueStockCheck";
import {
  pickInventorySupplierFields,
  resolveSupplierForIssueInvoice,
} from "../../../utils/medicineIssueSupplierUtils";
import {
  STANDARD_FORM_DIALOG_PAPER_SX,
  STANDARD_FORM_DIALOG_TITLE_SX,
  STANDARD_FORM_DIALOG_ACTIONS_SX,
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
} from "../../../utils/formModalStyles";
import {
  buildInvoiceDocumentHtml,
  printPatientSummaryDocument,
} from "../../../utils/patientSummaryDocumentUtils";
import PaymentModeSelector from "../../../components/PaymentModeSelector";
import {
  InvoiceAttachmentControl,
  InvoiceTotalsBox,
  InvoiceRemarksField,
  calcInvoiceGstBreakdown,
  resolveAttachmentUrl,
} from "../../../components/InvoiceFormShared";
import { useOrganizationGstPercentage } from "../../../hooks/useOrganizationGstPercentage";
import { uploadAttachment } from "../../../services/uploadService";

const calculateTotalDosage = (dosage) => {
  if (!dosage) return 0;
  return dosage.split("-").reduce((sum, num) => sum + parseInt(num.trim() || 0, 10), 0);
};

const IpdIssueMedicineModal = ({
  open,
  onClose,
  treatment,
  onSubmit,
  patientId,
  patientName,
  doctorName,
  doctorId = null,
  admissionId = null,
  departmentId = null,
  prescriptionId = null,
}) => {
  const { user } = useAuth();
  const { isNearExpiry, months: expiryThresholdMonths } = usePharmacyExpiryThreshold();
  const gstPercentage = useOrganizationGstPercentage(user, { enabled: open });
  const [duration, setDuration] = useState(treatment?.duration || "");
  const [durationError, setDurationError] = useState("");
  const [totalAmount, setTotalAmount] = useState(0);
  const [sgst, setSgst] = useState(0);
  const [cgst, setCgst] = useState(0);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [invoiceCreated, setInvoiceCreated] = useState(false); // Print only after Confirm Invoice succeeds
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [loading, setLoading] = useState(false);
  const [discount, setDiscount] = useState(0); // State for discount
  const [paymentMode, setPaymentMode] = useState({ mode: "cash" });
  const [particulars, setParticulars] = useState("");
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentName, setAttachmentName] = useState("");
  const [inventoryList, setInventoryList] = useState([]);
  const [stockChecking, setStockChecking] = useState(false);
  const [stockCheck, setStockCheck] = useState(null);
  const [stockLoadError, setStockLoadError] = useState("");

  useEffect(() => {
    if (treatment) {
      setDuration(treatment.duration || "");
    }
  }, [treatment]);

  useEffect(() => {
    if (open) {
      setDiscount(0);
      setPaymentMode({ mode: "cash" });
      setParticulars("");
      setAttachmentFile(null);
      setAttachmentName("");
      setReviewDialogOpen(false);
      setInvoiceCreated(false);
    }
  }, [open]);

  useEffect(() => {
    let cancelled = false;
    const loadInventory = async () => {
      if (!open) return;
      setStockChecking(true);
      setStockLoadError("");
      try {
        const list = await getMedicineInventoryList();
        if (!cancelled) {
          setInventoryList(Array.isArray(list) ? list : []);
        }
      } catch (err) {
        console.error("[IpdIssueMedicineModal] Failed to load inventory for stock check:", err);
        if (!cancelled) {
          setInventoryList([]);
          setStockLoadError(
            "Unable to verify medicine inventory stock. Confirm Issue is disabled until stock can be checked."
          );
        }
      } finally {
        if (!cancelled) setStockChecking(false);
      }
    };
    loadInventory();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !treatment) {
      setStockCheck(null);
      return;
    }
    if (stockLoadError) {
      setStockCheck(null);
      return;
    }
    const medicineId = treatment.medicineId || treatment.id;
    const requiredQty =
      calculateTotalDosage(treatment.dosage) * Math.max(0, Number(duration || 0));
    setStockCheck(
      evaluateMedicineIssueStock({
        medicineId,
        medicineName: treatment.medicine,
        requiredQty,
        inventoryList,
        allowMissingExpiry: false,
      })
    );
  }, [open, treatment, duration, inventoryList, stockLoadError]);

  const showAlert = (message, type = "success") => {
    setAlertMessage(message);
    setAlertType(type);
    setAlertOpen(true);
  };

  const handleCloseAlert = () => {
    setAlertOpen(false);
    setAlertMessage("");
  };

  const handlePrint = () => {
    const discountedAmount = totalAmount - (discount || 0);
    const { sgst: sgstAmount, cgst: cgstAmount, totalAmount: finalAmount } =
      calcInvoiceGstBreakdown(discountedAmount, gstPercentage);
    const halfGstLabel = (Number(gstPercentage) / 2).toFixed(1);
    const bodyHtml = buildInvoiceDocumentHtml({
      hospitalName: getOrganizationName(user) || "Hospital",
      title: "IPD MEDICINE INVOICE",
      metaLines: [
        { label: "Patient ID", value: patientId || "N/A" },
        { label: "Doctor", value: doctorName || "N/A" },
        { label: "Date", value: new Date().toLocaleDateString() },
      ],
      tableHeaders: [
        "Medicine Name",
        "Batch Number",
        "Expiry Date",
        "Quantity",
        "Cost/Unit (Rs)",
        "Total Cost (Rs)",
      ],
      tableRows: invoiceDetails
        ? [[
            invoiceDetails.medicine || "N/A",
            invoiceDetails.batchNumber || "N/A",
            invoiceDetails.expiryDate ? new Date(invoiceDetails.expiryDate).toLocaleDateString() : "N/A",
            `${invoiceDetails.totalQuantity} units`,
            Number(invoiceDetails.costPerUnit || 0).toFixed(2),
            Number(invoiceDetails.totalCost || 0).toFixed(2),
          ]]
        : [],
      summaryLines: [
        { label: "Base Amount", value: `Rs ${totalAmount.toFixed(2)}` },
        ...(discount > 0 ? [{ label: "Discount", value: `Rs ${discount.toFixed(2)}` }] : []),
        { label: "Amount After Discount", value: `Rs ${discountedAmount.toFixed(2)}` },
        { label: `SGST (${halfGstLabel}%)`, value: `Rs ${sgstAmount.toFixed(2)}` },
        { label: `CGST (${halfGstLabel}%)`, value: `Rs ${cgstAmount.toFixed(2)}` },
        { label: "Total Amount", value: `Rs ${finalAmount.toFixed(2)}` },
      ],
      notes: "This is a computer-generated invoice and does not require a signature.",
    });

    printPatientSummaryDocument({
      title: `IPD_Invoice_Patient_${patientId}_${new Date().toISOString().slice(0, 10)}`,
      bodyHtml,
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Validate duration
      if (!duration || Number(duration) < 1) {
        setDurationError("Duration must be at least 1 day");
        showAlert("Duration must be at least 1 day.", "error");
        return;
      }
      setDurationError("");

      if (!treatment?.medicine) {
        showAlert("No medicine found in treatment.", "error");
        return;
      }

      if (stockLoadError || stockChecking) {
        showAlert(
          stockLoadError || "Still verifying inventory stock. Please wait.",
          "error"
        );
        return;
      }

      if (stockCheck?.blocking) {
        showAlert(
          `Cannot confirm issue until stock issues are resolved. ${stockCheck.message}`,
          "error"
        );
        return;
      }

      // Get medicine ID from treatment (assuming it's available)
      let medicineId =
        stockCheck?.resolvedMedicineId ??
        treatment.medicineId ??
        treatment.id ??
        null;
      if (!medicineId && !treatment.medicine) {
        throw new Error(`Medicine ID is undefined for ${treatment.medicine}`);
      }

      // Fetch batch numbers and expiry dates from full inventory list
      let batchNumber = treatment.batchNumber || "N/A";
      let expiryDate = treatment.expiryDate || null;
      let inventoryId = null;
      let supplierFields = {
        supplierId: null,
        supplierName: null,
        stockInvoiceId: null,
      };
      
      try {
        // Fetch full medicine inventory list to get complete records with expiry dates
        const listForBatch = inventoryList?.length
          ? inventoryList
          : await getMedicineInventoryList();
        
        if (listForBatch && listForBatch.length > 0) {
          // Match by medicineId and/or name (covers duplicate catalog IDs)
          const match = resolveMedicineInventoryMatch(
            listForBatch,
            {
              medicineId,
              medicineName: treatment.medicine,
            },
            { allowMissingExpiry: false }
          );
          if (match.resolvedMedicineId != null) {
            medicineId = match.resolvedMedicineId;
          }

          const medicineInventory = match.items.filter((item) =>
            isUsableInventoryBatch(item, { allowMissingExpiry: false })
          );
          
          if (medicineInventory.length > 0) {
            // Sort by expiry date (FEFO - First Expiry First Out)
            const sortedInventory = medicineInventory.sort((a, b) => {
              const dateA = new Date(a.expiryDate || a.expiry_date || a.expirydate);
              const dateB = new Date(b.expiryDate || b.expiry_date || b.expirydate);
              return dateA - dateB; // Earliest expiry first (but all are future dates)
            });
            
            // Get the batch that will expire first (among non-expired batches)
            const selectedBatch = sortedInventory[0];
            batchNumber = selectedBatch.batchNumber || selectedBatch.batch_number || "N/A";
            expiryDate = selectedBatch.expiryDate || selectedBatch.expiry_date || selectedBatch.expirydate || null;
            inventoryId = selectedBatch.id || null;
            const batchMedicineId =
              selectedBatch.medicineId ?? selectedBatch.medicine_id;
            if (batchMedicineId != null) {
              medicineId = batchMedicineId;
            }
            supplierFields = pickInventorySupplierFields(selectedBatch);
          }
        }
      } catch (inventoryError) {
        console.error(`Error fetching inventory data for ${treatment.medicine}:`, inventoryError);
      }

      if (!medicineId) {
        throw new Error(`Medicine ID is undefined for ${treatment.medicine}`);
      }

      // Fetch cost per unit
      const costPerUnit = await getCostPerUnitById(medicineId);
      if (costPerUnit === undefined || costPerUnit === null) {
        throw new Error(`Cost per unit not found for ${treatment.medicine}`);
      }

      const totalQuantity = calculateTotalDosage(treatment.dosage) * Number(duration);
      const totalCost = totalQuantity * costPerUnit;

      if (totalQuantity <= 0) {
        showAlert("Total quantity must be greater than 0.", "error");
        return;
      }

      // Prepare medicine data
      const medicineData = {
        ...treatment,
        medicineId: medicineId,
        duration: duration,
        totalQuantity,
        costPerUnit,
        totalCost,
        batchNumber: batchNumber,
        expiryDate: expiryDate,
        medicineInventoryId: inventoryId,
        manufacturer: treatment.manufacturer || "N/A",
        medicineType: treatment.medicineType || "N/A",
        supplierId: supplierFields.supplierId,
        supplierName: supplierFields.supplierName,
        stockInvoiceId: supplierFields.stockInvoiceId,
      };

      setTotalAmount(totalCost);

      const { sgst: calculatedSgst, cgst: calculatedCgst } = calcInvoiceGstBreakdown(
        totalCost,
        gstPercentage
      );
      setSgst(calculatedSgst);
      setCgst(calculatedCgst);

      setPaymentMode({ mode: "cash" });
      setParticulars("");
      setAttachmentFile(null);
      setAttachmentName("");
      setInvoiceDetails(medicineData);
      setInvoiceCreated(false);
      setReviewDialogOpen(true);

    } catch (error) {
      console.error("Error processing medicine:", error);
      showAlert(`Failed to process medicine: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmInvoice = async () => {
    try {
      setLoading(true);

      if (!paymentMode?.mode) {
        showAlert("Please select a payment mode.", "error");
        setLoading(false);
        return;
      }
      if (paymentMode.mode === "credit" && !paymentMode.organizationId) {
        showAlert("Please select an organization for credit payment.", "error");
        setLoading(false);
        return;
      }
      
      // Calculate final amount after discount
      const amountAfterDiscount = totalAmount - (discount || 0);
      const {
        sgst: calculatedSgst,
        cgst: calculatedCgst,
        totalAmount: totalInvoiceAmount,
      } = calcInvoiceGstBreakdown(amountAfterDiscount, gstPercentage);

      let attachmentUrl = null;
      if (attachmentFile instanceof File) {
        const uploaded = await uploadAttachment(attachmentFile);
        attachmentUrl = resolveAttachmentUrl(uploaded);
      }

      const paymentModeValue = (paymentMode?.mode || "cash").toUpperCase();
      const creditOrganizationId =
        paymentModeValue === "CREDIT"
          ? paymentMode?.organizationId || null
          : null;

      const hospitalOrgId = getOrganizationId(user);
      const hospitalFromName = getOrganizationName(user) || "Aryan Hospital";

      // Copy supplier from stock purchase invoice (via inventory batch) so
      // transaction history can resolve supplier name from this issue invoice.
      const supplierInfo = await resolveSupplierForIssueInvoice(invoiceDetails);

      // Create invoice
      const resolvedAdmissionId =
        admissionId ||
        treatment?.admissionId ||
        treatment?.admission_id ||
        null;
      const resolvedDoctorId =
        doctorId ||
        treatment?.doctorId ||
        treatment?.doctor_id ||
        null;

      const resolvedDepartmentId = await resolveInvoiceDepartmentId({
        departmentId,
        doctorId: resolvedDoctorId,
        admissionId: resolvedAdmissionId,
      });

      const resolvedPrescriptionId = resolveInvoicePrescriptionId(
        prescriptionId,
        treatment?.prescriptionId,
        treatment?.prescription_id,
        treatment?.ipdPrescriptionId,
        treatment?.ipd_prescription_id
      );

      const invoice = await createInvoice({
        amount: totalAmount, // Base amount (before discount and tax)
        discount: discount || 0, // Discount amount
        sgst: calculatedSgst,
        cgst: calculatedCgst,
        totalAmount: totalInvoiceAmount, // Total amount including tax after discount
        date: new Date().toISOString(),
        particulars: particulars?.trim() || "IPD MEDICINE",
        invoiceType: "MEDICINE",
        patientId: patientId,
        doctorId:
          resolvedDoctorId != null && resolvedDoctorId !== ""
            ? Number(resolvedDoctorId)
            : undefined,
        admissionId:
          resolvedAdmissionId != null && resolvedAdmissionId !== ""
            ? Number(resolvedAdmissionId)
            : undefined,
        prescriptionId: resolvedPrescriptionId,
        departmentId: resolvedDepartmentId,
        fromName: hospitalFromName,
        toName: patientName || treatment?.patientName || null,
        organizationId: hospitalOrgId || undefined,
        paymentMode: paymentModeValue,
        creditOrganizationId,
        supplierId: supplierInfo.supplierId || null,
        ...(supplierInfo.supplierName
          ? { supplierName: supplierInfo.supplierName }
          : {}),
        ...(attachmentUrl ? { attachment: attachmentUrl } : {}),
      });

      // Prepare transaction data
      const transactionData = {
        invoiceId: parseInt(invoice.id) || 0,
        patientId: parseInt(patientId) || 0,
        userId: parseInt(user?.id) || 0,
        prescriptionId: resolvedPrescriptionId || undefined,
        patientType: "ipd", // IPD patient type
        transactionType: "OUT",
        transactionDate: new Date().toISOString(),
        doctorName: doctorName || null, // ✅ Pass doctor name to backend
        medicines: [{
          medicineId: parseInt(invoiceDetails.medicineId) || 0,
          medicineInventoryId: parseInt(invoiceDetails.medicineInventoryId) || null,
          medicineName: invoiceDetails.medicine || "",
          dosage: invoiceDetails.dosage || "",
          duration: parseInt(duration) || 0,
          costPerUnit: parseFloat(invoiceDetails.costPerUnit) || 0.0,
          totalCost: parseFloat(invoiceDetails.totalCost) || 0.0,
          batchNumber: invoiceDetails.batchNumber || "N/A",
          expiryDate: invoiceDetails.expiryDate || null,
          manufacturer: invoiceDetails.manufacturer || "N/A",
          medicineType: invoiceDetails.medicineType || "N/A",
        }]
      };

      try {
        // Deduct inventory with transaction tracking
        await deductMedicineInventoryWithHistory(transactionData);

        if (invoiceDetails.expiryDate && isNearExpiry(invoiceDetails.expiryDate)) {
          await triggerNearExpiryLowStockAlert({
            medicineId: invoiceDetails.medicineId,
            medicineName: invoiceDetails.medicine,
            batchNumber: invoiceDetails.batchNumber,
            expiryDate: invoiceDetails.expiryDate,
            quantity: invoiceDetails.totalQuantity,
            source: "IPD_ISSUE",
          });
          showAlert(
            `IPD medicine issued. Batch expires within ${formatExpiryThresholdLabel(expiryThresholdMonths)} — consider reordering.`,
            "warning"
          );
        } else {
          showAlert("IPD Medicine issued and inventory updated successfully", "success");
        }

        setInvoiceCreated(true);
        onSubmit(invoiceDetails);
        // Keep review dialog open so user can Print Invoice after creation
        
      } catch (transactionError) {
        console.warn("Transaction tracking failed:", transactionError);
        throw transactionError;
      }
      
    } catch (error) {
      console.error("Error creating IPD invoice:", error);
      
      let errorMessage = "Failed to create IPD invoice";
      if (error.response) {
        errorMessage = `Server error: ${error.response.status} - ${error.response.data?.message || error.response.statusText}`;
      } else if (error.request) {
        errorMessage = "Network error: No response from server";
      } else {
        errorMessage = `Error: ${error.message}`;
      }
      
      showAlert(errorMessage, "error");
    } finally {
      setLoading(false);
      // Keep review dialog open after successful create (for Print Invoice)
    }
  };

  if (!treatment) {
    return null;
  }

  return (
    <>
      {/* Main Issue Medicine Dialog */}
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        sx={STANDARD_FORM_DIALOG_PAPER_SX}
      >
        <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>
          Issue IPD Medicine
        </DialogTitle>
        
        <DialogContent>
          {/* Treatment Information */}
          <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* <MedicationIcon color="primary" /> */}
              Treatment Details
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip label={`Type: ${treatment.treatmentType}`} color="primary" variant="outlined" size="small" />
              <Chip label={`By: ${treatment.performedByName}`} color="secondary" variant="outlined" size="small" />
              {treatment.treatmentDate && (
                <Chip label={`Date: ${new Date(treatment.treatmentDate).toLocaleDateString()}`} color="default" variant="outlined" size="small" />
              )}
            </Box>
          </Box>

          {stockLoadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Stock check unavailable</AlertTitle>
              {stockLoadError}
            </Alert>
          )}
          {!stockLoadError && stockChecking && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Verifying medicine inventory stock…
            </Alert>
          )}
          {!stockLoadError && !stockChecking && stockCheck?.blocking && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Attention — cannot confirm issue</AlertTitle>
              {stockCheck.message}
            </Alert>
          )}
          {!stockLoadError && !stockChecking && stockCheck && !stockCheck.blocking && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Inventory stock is sufficient for this issue quantity.
            </Alert>
          )}

          {/* Medicine Table */}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Medicine</TableCell>
                <TableCell>Dosage</TableCell>
                <TableCell>Frequency</TableCell>
                <TableCell>Duration (days)</TableCell>
                <TableCell>Total Quantity</TableCell>
                <TableCell>Stock</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow hover>
                <TableCell>
                  <Box>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {/* <MedicationIcon color="primary" fontSize="small" /> */}
                      {treatment.medicine}
                    </Typography>
                    {treatment.remarks && (
                      <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                        <strong>Remarks:</strong> {treatment.remarks}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <TextField
                    value={treatment.dosage}
                    disabled
                    size="small"
                    fullWidth
                    sx={{ maxWidth: '100px' }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={treatment.frequency || "As prescribed"}
                    disabled
                    size="small"
                    fullWidth
                    sx={{ maxWidth: '120px' }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    value={duration}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (isNaN(value)) {
                        setDuration("");
                        setDurationError("");
                        return;
                      }
                      setDuration(value);
                      setDurationError("");
                    }}
                    error={!!durationError}
                    helperText={durationError}
                    size="small"
                    inputProps={{ min: 1 }}
                    sx={{ maxWidth: '100px' }}
                    required
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold" color="primary">
                    {calculateTotalDosage(treatment.dosage) * (Math.max(0, Number(duration || 0)))} units
                  </Typography>
                </TableCell>
                <TableCell>
                  {stockCheck ? (
                    <Box>
                      <Chip
                        size="small"
                        label={stockStatusLabel(stockCheck.status)}
                        color={
                          stockCheck.status === "OK"
                            ? "success"
                            : stockCheck.status === "INSUFFICIENT_STOCK"
                              ? "warning"
                              : "error"
                        }
                        variant="outlined"
                      />
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        Available: {stockCheck.availableQty} / Need: {stockCheck.requiredQty}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {stockChecking ? "Checking…" : "—"}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </DialogContent>
        
        <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
          <Button onClick={onClose} color="primary" variant="outlined" sx={FORM_CANCEL_BUTTON_SX} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            color="primary" 
            variant="contained"
            sx={FORM_SUBMIT_BUTTON_SX}
            disabled={
              !treatment.medicine ||
              loading ||
              stockChecking ||
              !!stockLoadError ||
              !!stockCheck?.blocking ||
              !duration ||
              Number(duration) < 1
            }
          >
            {loading ? "Processing..." : "Confirm Issue"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Invoice Dialog */}
      <Dialog 
        open={reviewDialogOpen} 
        onClose={() => setReviewDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        sx={STANDARD_FORM_DIALOG_PAPER_SX}
      >
        <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>
          Review IPD Invoice Details
        </DialogTitle>
        
        <DialogContent>
          <Box>
            {invoiceDetails && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Medicine Details</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ py: 1 }}>Medicine Name</TableCell>
                      <TableCell sx={{ py: 1 }}>Batch Number</TableCell>
                      <TableCell sx={{ py: 1 }}>Expiry Date</TableCell>
                      <TableCell sx={{ py: 1 }}>Quantity</TableCell>
                      <TableCell sx={{ py: 1 }}>Cost/Unit</TableCell>
                      <TableCell sx={{ py: 1 }}>Total Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ py: 1 }}>{invoiceDetails.medicine}</TableCell>
                      <TableCell sx={{ py: 1 }}>{invoiceDetails.batchNumber || 'N/A'}</TableCell>
                      <TableCell sx={{ py: 1 }}>
                        {invoiceDetails.expiryDate 
                          ? new Date(invoiceDetails.expiryDate).toLocaleDateString() 
                          : 'N/A'}
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>{invoiceDetails.totalQuantity} units</TableCell>
                      <TableCell sx={{ py: 1 }}>Rs {invoiceDetails.costPerUnit?.toFixed(2)}</TableCell>
                      <TableCell sx={{ py: 1 }}>Rs {invoiceDetails.totalCost?.toFixed(2)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            )}
            
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Invoice Summary</Typography>
              <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="body2" fontWeight="bold">Discount (₹):</Typography>
                <TextField
                  type="number"
                  value={discount}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    setDiscount(Math.max(0, Math.min(value, totalAmount)));
                  }}
                  size="small"
                  inputProps={{ min: 0, max: totalAmount, step: 0.01 }}
                  sx={{ width: "120px" }}
                  disabled={loading || invoiceCreated}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 0.5 }}>₹</Typography>,
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  Base: ₹{totalAmount.toFixed(2)}
                  {discount > 0 ? ` → After discount: ₹${(totalAmount - discount).toFixed(2)}` : ""}
                </Typography>
              </Box>
              <InvoiceTotalsBox
                subtotal={totalAmount - (discount || 0)}
                gstPercentage={gstPercentage}
              />
              <Box sx={{ mt: 1.5 }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <PaymentModeSelector
                      value={paymentMode}
                      onChange={setPaymentMode}
                      disabled={loading || invoiceCreated}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InvoiceAttachmentControl
                      attachmentName={attachmentName}
                      attachmentFile={attachmentFile}
                      onFileChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setAttachmentFile(file);
                        setAttachmentName(file.name);
                      }}
                      onDelete={() => {
                        setAttachmentFile(null);
                        setAttachmentName("");
                      }}
                      disabled={loading || invoiceCreated}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ mt: 1.5 }}>
                <InvoiceRemarksField
                  name="particulars"
                  value={particulars}
                  onChange={(e) => setParticulars(e.target.value)}
                  disabled={loading || invoiceCreated}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ ...STANDARD_FORM_DIALOG_ACTIONS_SX, '@media print': { display: 'none' } }}>
          {invoiceCreated && (
            <Button 
              onClick={handlePrint} 
              color="primary" 
              variant="outlined"
              sx={FORM_CANCEL_BUTTON_SX}
            >
              Print Invoice
            </Button>
          )}
          <Button
            onClick={() => {
              setReviewDialogOpen(false);
              if (invoiceCreated) {
                onClose();
              }
            }}
            color="primary"
            variant="outlined"
            sx={FORM_CANCEL_BUTTON_SX}
            disabled={loading}
          >
            {invoiceCreated ? "Close" : "Cancel"}
          </Button>
          {!invoiceCreated && (
            <Button
              onClick={handleConfirmInvoice}
              color="primary"
              variant="contained"
              sx={FORM_SUBMIT_BUTTON_SX}
              disabled={
                loading ||
                !paymentMode?.mode ||
                (paymentMode?.mode === "credit" && !paymentMode?.organizationId)
              }
            >
              {loading ? "Processing..." : "Confirm Invoice"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Alert Popup */}
      <Dialog 
        open={alertOpen} 
        onClose={handleCloseAlert}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: 360,
            m: 2,
          },
        }}
      >
        <Alert 
          severity={alertType} 
          onClose={handleCloseAlert}
          sx={{ 
            fontSize: '0.875rem',
            py: 1,
            alignItems: 'flex-start',
            '& .MuiAlert-message': {
              width: '100%',
              py: 0.25,
            },
            '& .MuiAlert-icon': {
              py: 0.5,
              fontSize: '1.25rem',
            },
          }}
        >
          <AlertTitle sx={{ fontSize: '0.95rem', fontWeight: 600, mb: 0.5 }}>
            {alertType === "success" ? "Success!" : "Error!"}
          </AlertTitle>
          {alertMessage}
        </Alert>
      </Dialog>
    </>
  );
};

export default IpdIssueMedicineModal;
